import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Clock, Check, Plus, Heart } from 'lucide-react';
import { FaHeart, FaRegHeart } from 'react-icons/fa';

export default function TestCard({ 
  test, 
  inCart, 
  onCartToggle, 
  inWishlist, 
  onWishlistToggle, 
  className = '' 
}) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  const testId = test?.testID || test?.TestID || test?.id;
  const testName = test?.testName || test?.TestName || test?.name || 'Diagnostic Blood Test';
  const labName = test?.labName || test?.LabName || 'Certified Partner Lab';
  
  const sellingPrice = Number(test?.sellingPrice) || Number(test?.SellingPrice) || Number(test?.testPrice) || Number(test?.TestPrice) || 299;
  const originalPrice = Number(test?.testPrice) || Number(test?.TestPrice) || (test?.discount ? sellingPrice + Number(test.discount) : Math.round(sellingPrice * 1.4));
  const hasDiscount = originalPrice > sellingPrice;
  const discountPercent = hasDiscount ? Math.round(((originalPrice - sellingPrice) / originalPrice) * 100) : 0;

  const rawImage = test?.testImage || test?.TestImage || test?.image;
  let testImageUrl = null;
  if (rawImage && typeof rawImage === 'string' && rawImage.trim()) {
    testImageUrl = rawImage.startsWith('http') 
      ? rawImage 
      : 'https://admin.medrphalabs.com/TestMasterImage/' + rawImage.trim();
  }

  const reportTime = test?.ReportTime || test?.reportTime;
  const reportTimeType = test?.ReportTimeType || test?.reportTimeType;
  const reportText = reportTime ? ('⏱️ ' + reportTime + ' ' + (reportTimeType === 2 ? 'days' : 'hrs') + ' Report') : '⏱️ 24 hrs Report';

  return (
    <div
      onClick={() => navigate('/TestDetail/' + testId)}
      className={'bg-white border border-slate-200/90 hover:border-red-400 rounded-2xl p-3.5 flex flex-col justify-between hover:shadow-lg transition-all duration-300 relative group overflow-hidden cursor-pointer ' + className}
    >
      <div>
        {/* Top Image Box */}
        <div className='h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden'>
          
          {/* Discount Badge */}
          {hasDiscount && discountPercent > 0 && (
            <div className='absolute top-2 left-2 flex items-center gap-1 bg-[#E53935] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-2xs z-10'>
              <span>{discountPercent}% OFF</span>
            </div>
          )}

          {/* Wishlist Button */}
          {onWishlistToggle && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWishlistToggle(test, e);
              }}
              className='absolute top-2 right-2 p-1.5 rounded-full bg-white/90 backdrop-blur-xs shadow-xs hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-slate-100'
              title='Wishlist'
            >
              {inWishlist ? (
                <FaHeart size={12} className='text-red-500 fill-red-500' />
              ) : (
                <FaRegHeart size={12} className='text-slate-400 hover:text-red-500' />
              )}
            </button>
          )}

          {/* Fast Report Badge */}
          <div className='absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-blue-800 text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-xs border border-blue-100 z-10'>
            <span>{reportText}</span>
          </div>

          {/* Live Image or Name Fallback */}
          {!imgError && testImageUrl ? (
            <img
              src={testImageUrl}
              className='max-h-full max-w-full object-contain'
              alt={testName}
              onError={() => setImgError(true)}
            />
          ) : null}

          {/* Product Name Fallback */}
          <div 
            className={'name-fallback w-full h-full flex flex-col items-center justify-center text-center p-2.5 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ' + (!imgError && testImageUrl ? 'hidden' : 'flex')}
          >
            <div className='w-10 h-10 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-1.5 shadow-md'>
              <FlaskConical size={18} />
            </div>
            <span className='text-[11px] font-black text-slate-800 line-clamp-2 leading-tight text-center'>
              {testName}
            </span>
          </div>
        </div>

        {/* Test Information */}
        <div className='mt-3'>
          <div className='inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.2 rounded mb-1 truncate max-w-full'>
            <span>By {labName}</span>
          </div>

          <h4 className='text-xs sm:text-sm font-extrabold text-slate-900 group-hover:text-[#E53935] transition-colors line-clamp-2 leading-snug'>
            {testName}
          </h4>

          <div className='mt-2 flex items-baseline gap-1.5'>
            <span className='text-sm font-black text-slate-900'>
              ₹{sellingPrice}
            </span>
            {hasDiscount && (
              <span className='text-xs text-slate-400 line-through'>
                ₹{originalPrice}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Cart Button */}
      <div className='mt-3 pt-2.5 border-t border-slate-100'>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onCartToggle) onCartToggle(test);
          }}
          className={'w-full py-2 px-3 rounded-xl text-xs font-bold transition-all border flex items-center justify-center gap-1.5 shadow-2xs ' + (inCart ? 'bg-red-600 hover:bg-red-700 text-white border-red-600 shadow-sm' : 'bg-white text-red-600 border-red-600 hover:bg-red-50')}
        >
          {inCart ? (
            <>
              <Check size={13} />
              <span>ADDED IN CART</span>
            </>
          ) : (
            <>
              <Plus size={13} />
              <span>ADD TEST</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
